import React, { useEffect, useState } from "react"
import { formatDateDataGrid } from "src/utils/format-date"
import PropTypes from 'prop-types'
import dayjs from "dayjs"
import { DATE_FORMAT, DATE_TIME_FORMAT_SHOW } from "src/utils/constants"
import DatalakeService from "src/services/datalake-service"
import ParameterPaginationTable from "src/components/table/parameter-pagination-table"

const ParameterCBondsBondsDefaultedsTable = (props) => {
    const dataLakeService = new DatalakeService()
    const [data, setData] = useState([])
    const [pagination, setPagination] = useState({ count: 0, next: null, previous: null })

    useEffect(() => {
        loadData()
    }, [])

    const loadData = () => {
        props.loading(true)
        dataLakeService.loadPaginationEndpoint(props.endpoint).then((response) => {
            const { body } = response
            const { results, count, next, previous } = body
            const list = results.map((obj, index) => ({ ...obj, id: index }))
            setPagination({ count: count, next: next, previous: previous })
            setData(list)
        }).catch((err) => {
            props.callbackError(err)
        }).finally(() => {
            props.loading(false)
        })
    }

    const createRow = async () => {
        const body = {
            "DataDefault": dayjs().format(DATE_FORMAT),
            "IDPaisEmitente": null,
            "IDRegiaoPaisEmitente": null,
            "IDSubregiaoPaisEmitente": null,
            "IDEmitente": null,
            "IDEmissao": null,
            "ISIN": null,
            "DataEstimada": dayjs().format(DATE_FORMAT),
            "MaisInformacoesEng": null,
            "MaisInformacoesIta": null,
            "MaisInformacoesPol": null,
            "MaisInformacoesRus": null,
            "IDStatus": null,
            "NomeStatusEng": null,
            "NomeStatusIta": null,
            "NomeStatusPol": null,
            "NomeStatusRus": null,
            "IDTipo": null,
            "NomeTipoEng": null,
            "NomeTipoIta": null,
            "NomeTipoRus": null,
            "DataAtualizacao": dayjs().format(DATE_FORMAT),
        }

        props.loading(true)
        return dataLakeService.createTableEndpoint(props.endpoint, body).then(() => {
            props.loading(false)
            loadData(0, 25)
        }).catch((err) => {
            props.loading(false)
            throw err
        })
    }

    const updateRow = async (newRow) => {
        const { ID, DataDefault, IDPaisEmitente, IDRegiaoPaisEmitente, IDSubregiaoPaisEmitente, IDEmitente, IDEmissao, ISIN, DataEstimada, MaisInformacoesEng, MaisInformacoesIta, MaisInformacoesPol, MaisInformacoesRus, IDStatus, NomeStatusEng, NomeStatusIta, NomeStatusPol, NomeStatusRus, IDTipo, NomeTipoEng, NomeTipoIta, NomeTipoRus, DataAtualizacao
        } = newRow

        const body = {
            "DataDefault": DataDefault,
            "IDPaisEmitente": IDPaisEmitente,
            "IDRegiaoPaisEmitente": IDRegiaoPaisEmitente,
            "IDSubregiaoPaisEmitente": IDSubregiaoPaisEmitente,
            "IDEmitente": IDEmitente,
            "IDEmissao": IDEmissao,
            "ISIN": ISIN,
            "DataEstimada": DataEstimada,
            "MaisInformacoesEng": MaisInformacoesEng,
            "MaisInformacoesIta": MaisInformacoesIta,
            "MaisInformacoesPol": MaisInformacoesPol,
            "MaisInformacoesRus": MaisInformacoesRus,
            "IDStatus": IDStatus,
            "NomeStatusEng": NomeStatusEng,
            "NomeStatusIta": NomeStatusIta,
            "NomeStatusPol": NomeStatusPol,
            "NomeStatusRus": NomeStatusRus,
            "IDTipo": IDTipo,
            "NomeTipoEng": NomeTipoEng,
            "NomeTipoIta": NomeTipoIta,
            "NomeTipoRus": NomeTipoRus,
            "DataAtualizacao": DataAtualizacao
        }

        props.loading(true)
        return dataLakeService.updateTableEndpoint(props.endpoint, ID, body).then(() => {
            props.loading(false)
        }).catch((err) => {
            props.loading(false)
            throw err
        })
    }

    const deleteRow = async (row) => {
        props.loading(true)
        const { ID } = row
        return dataLakeService.deleteTableEndpoint(props.endpoint, ID).then(() => {
            props.loading(false)
            const listRemoved = data.filter((item) => item.ID !== ID)
            setData(listRemoved)
        }).catch((err) => {
            props.loading(false)
            throw err
        })
    }

    const onFilter = async (filterList, limit, offset) => {
        const query = filterList.map(obj => `${encodeURIComponent(obj.field)}=${encodeURIComponent(obj.value)}`).join('&')

        props.loading(true)
        dataLakeService.loadPaginationEndpoint(props.endpoint, query, limit, offset).then((response) => {
            props.loading(false)
            const { body } = response
            const { results, count, next, previous } = body
            const list = results.map((obj) => ({ ...obj, id: obj.ID }))
            setPagination({ count: count, next: next, previous: previous })
            setData(list)
        })
    }

    const changePage = async (endpoint) => {
        props.loading(true)
        dataLakeService.paginate(endpoint).then((response) => {
            props.loading(false)
            const { body } = response
            const { results, count, previous, next } = body
            const list = results.map((obj) => ({ ...obj, id: obj.ID }))

            setPagination({ count: count, next: next, previous: previous })
            setData([])
            setData(list)
        })
    }

    const columns = [
        {
            field: 'ID',
            headerName: '#',
            disableColumnMenu: true,
            disableFilter: true,
            flex: true,
            minWidth: 80,
        },
        {
            field: 'DataDefault',
            headerName: 'DataDefault',
            disableColumnMenu: true,
            flex: 1,
            minWidth: 150,
            typeComponent: 'date',
            valueGetter: ({ value }) => (value ? new Date(value) : null),
            valueFormatter: ({ value }) => {
                if (!value) return ''
                return dayjs.utc(value).format(DATE_FORMAT)
            }
        },
        {
            field: 'IDPaisEmitente',
            headerName: 'IDPaisEmitente',
            disableColumnMenu: true,
            flex: true,
            minWidth: 150,
            type: 'number'
        },
        {
            field: 'IDRegiaoPaisEmitente',
            headerName: 'IDRegiaoPaisEmitente',
            disableColumnMenu: true,
            flex: true,
            minWidth: 150,
            type: 'number'
        },
        {
            field: 'IDSubregiaoPaisEmitente',
            headerName: 'IDSubregiaoPaisEmitente',
            disableColumnMenu: true,
            flex: true,
            minWidth: 150,
            type: 'number'
        },
        {
            field: 'IDEmitente',
            headerName: 'IDEmitente',
            disableColumnMenu: true,
            flex: true,
            minWidth: 150,
            type: 'number'
        },
        {
            field: 'IDEmissao',
            headerName: 'IDEmissao',
            disableColumnMenu: true,
            flex: true,
            minWidth: 150,
            type: 'number'
        },
        {
            field: 'ISIN',
            headerName: 'ISIN',
            disableColumnMenu: true,
            flex: true,
            minWidth: 150
        },
        {
            field: 'DataEstimada',
            headerName: 'DataEstimada',
            disableColumnMenu: true,
            flex: true,
            minWidth: 150,
            typeComponent: 'date',
            valueGetter: ({ value }) => (value ? new Date(value) : null),
            valueFormatter: ({ value }) => {
                if (!value) return ''
                return dayjs.utc(value).format(DATE_FORMAT)
            }
        },
        {
            field: 'MaisInformacoesEng',
            headerName: 'MaisInformacoesEng',
            disableColumnMenu: true,
            flex: true,
            minWidth: 150
        },
        {
            field: 'MaisInformacoesIta',
            headerName: 'MaisInformacoesIta',
            disableColumnMenu: true,
            flex: true,
            minWidth: 150
        },
        {
            field: 'MaisInformacoesPol',
            headerName: 'MaisInformacoesPol',
            disableColumnMenu: true,
            flex: true,
            minWidth: 150
        },
        {
            field: 'MaisInformacoesRus',
            headerName: 'MaisInformacoesRus',
            disableColumnMenu: true,
            flex: true,
            minWidth: 150
        },
        {
            field: 'IDStatus',
            headerName: 'IDStatus',
            disableColumnMenu: true,
            flex: true,
            minWidth: 150,
            type: 'number'
        },
        {
            field: 'NomeStatusEng',
            headerName: 'NomeStatusEng',
            disableColumnMenu: true,
            flex: true,
            minWidth: 150
        },
        {
            field: 'NomeStatusIta',
            headerName: 'NomeStatusIta',
            disableColumnMenu: true,
            flex: true,
            minWidth: 150
        },
        {
            field: 'NomeStatusPol',
            headerName: 'NomeStatusPol',
            disableColumnMenu: true,
            flex: true,
            minWidth: 150
        },
        {
            field: 'NomeStatusRus',
            headerName: 'NomeStatusRus',
            disableColumnMenu: true,
            flex: true,
            minWidth: 150
        },
        {
            field: 'IDTipo',
            headerName: 'IDTipo',
            disableColumnMenu: true,
            flex: true,
            minWidth: 150,
            type: 'number'
        },
        {
            field: 'NomeTipoEng',
            headerName: 'NomeTipoEng',
            disableColumnMenu: true,
            flex: true,
            minWidth: 150
        },
        {
            field: 'NomeTipoIta',
            headerName: 'NomeTipoIta',
            disableColumnMenu: true,
            flex: true,
            minWidth: 150
        },
        {
            field: 'NomeTipoRus',
            headerName: 'NomeTipoRus',
            disableColumnMenu: true,
            flex: true,
            minWidth: 150
        },
        {
            field: 'DataAtualizacao',
            headerName: 'DataAtualizacao',
            disableColumnMenu: true,
            flex: true,
            minWidth: 150,
            type: 'dateTime',
            valueGetter: ({ value }) => (value ? new Date(value) : null),
            valueFormatter: ({ value }) => {
                if (!value) return ''
                return dayjs.utc(value).format(DATE_TIME_FORMAT_SHOW)
            }
        },
        {
            field: 'CriadoEm',
            headerName: 'Data de Criação',
            flex: true,
            minWidth: 150,
            disableColumnMenu: true,
            disableFilter: true,
            renderCell: (params) => formatDateDataGrid(params)
        },
        {
            field: 'AtualizadoEm',
            headerName: 'Data de Atualização',
            flex: true,
            minWidth: 150,
            disableColumnMenu: true,
            disableFilter: true,
            renderCell: (params) => formatDateDataGrid(params)
        }
    ]

    return (
        <div>
            <ParameterPaginationTable
                columns={columns}
                data={data}
                setData={setData}
                createRow={createRow}
                deleteRow={deleteRow}
                updateRow={updateRow}
                filterFunction={onFilter}
                pagination={pagination}
                onChangePage={changePage}
                customFilter={true} />
        </div>
    )
}

ParameterCBondsBondsDefaultedsTable.propTypes = {
    loading: PropTypes.func,
    endpoint: PropTypes.string,
    callbackError: PropTypes.func
}

export default ParameterCBondsBondsDefaultedsTable